import React, { useEffect, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { getEventImageUrl } from '../utils/eventImage';

export default function PastEventItems() {
  const { strings } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadPastEvents = async () => {
      try {
        const eventsUrl = process.env.REACT_APP_EVENTS_URL;
        if (!eventsUrl) throw new Error('Events service URL is not configured.');

        const response = await fetch(eventsUrl);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        const allEvents = Array.isArray(data) ? data : data.events || data.data || [];
        if (!Array.isArray(allEvents)) throw new Error('Unexpected data format received from API');

        const inactiveEvents = allEvents.filter((event) => Number(event.active) === 0);
        const detailedEvents = await Promise.all(inactiveEvents.map(async (event) => {
          const detailUrl = new URL(eventsUrl, window.location.href);
          detailUrl.searchParams.set('id', event.id);
          const detailResponse = await fetch(detailUrl.toString());
          if (!detailResponse.ok) throw new Error(`Could not load event ${event.id}.`);
          const detailData = await detailResponse.json();
          if (!detailData.ok || !detailData.event) {
            throw new Error(detailData.error || `Could not load event ${event.id}.`);
          }
          return { ...event, ...detailData.event, pastEvents: detailData.event.pastEvents || [] };
        }));

        detailedEvents.sort((first, second) => (
          new Date(second.startTime || second.start_time).getTime()
          - new Date(first.startTime || first.start_time).getTime()
        ));
        setEvents(detailedEvents);
      } catch (err) {
        setError(err.message || 'Error loading past events');
      } finally {
        setLoading(false);
      }
    };

    loadPastEvents();
  }, []);

  const formatDate = (startDateStr, endDateStr) => {
    if (!startDateStr) return { day: '--', month: '---', endDay: '', endMonth: '' };
    const startDate = new Date(startDateStr);
    const locale = strings.lang_code === 'RU' ? 'ru' : 'en';
    const day = startDate.getDate().toString().padStart(2, '0');
    const month = startDate.toLocaleString(locale, { month: 'short' }).toUpperCase();
    let endDay = '';
    let endMonth = '';

    if (endDateStr) {
      const endDate = new Date(endDateStr);
      if (startDate.toDateString() !== endDate.toDateString()) {
        endDay = endDate.getDate().toString().padStart(2, '0');
        endMonth = endDate.toLocaleString(locale, { month: 'short' }).toUpperCase();
      }
    }

    return { day, month, endDay, endMonth };
  };

  if (loading) {
    return (
      <div className="content-wrapper" style={{ justifyContent: 'center', padding: '40px 0' }}>
        <p style={{ opacity: 0.7 }}>Loading past events...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-wrapper" style={{ justifyContent: 'center', padding: '40px 0' }}>
        <p style={{ color: '#e74c3c' }}>{error}</p>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="content-wrapper" style={{ justifyContent: 'center', padding: '40px 0', textAlign: 'center' }}>
        <p style={{ opacity: 0.8, fontSize: '1.1rem' }}>
          {strings.lang_code === 'RU' ? 'Прошедшие мероприятия: пока нет.' : 'No past events found.'}
        </p>
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      {events.map((event) => {
        const startTime = event.startTime || event.start_time;
        const endTime = event.endTime || event.end_time;
        const coverImage = getEventImageUrl(event.imagePath || event.image_path || event.image);
        const description = event.description;
        const pastPhotos = event.pastEvents || [];
        const { day, month, endDay, endMonth } = formatDate(startTime, endTime);

        return (
          <article
            key={event.id}
            className="service-card event"
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
          >
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '16px' }}>
                <span style={{ fontSize: '2.5rem', fontWeight: '800', color: '#d4af37', lineHeight: '1' }}>
                  {day}
                </span>
                <span style={{ fontSize: '0.9rem', fontWeight: '700', textTransform: 'uppercase' }}>
                  {month}
                </span>
                {endDay && (
                  <>
                    <span style={{ fontSize: '0.9rem', fontWeight: '700' }}>–</span>
                    <span style={{ fontSize: '2.5rem', fontWeight: '800', color: '#d4af37', lineHeight: '1' }}>
                      {endDay}
                    </span>
                    <span style={{ fontSize: '0.9rem', fontWeight: '700', textTransform: 'uppercase' }}>
                      {endMonth}
                    </span>
                  </>
                )}
              </div>

              <h3
                className="service-name"
                style={{
                  fontSize: '20px',
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 400,
                  marginBottom: '12px',
                }}
              >
                {event.title}
              </h3>

              {(coverImage || description) && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '16px' }}>
                  {coverImage && (
                    <img
                      src={coverImage}
                      alt={`${event.title} poster`}
                      style={{
                        display: 'block',
                        flex: '0 0 min(33%, 112px)',
                        width: 'min(33%, 112px)',
                        aspectRatio: '210 / 297',
                        objectFit: 'cover',
                        borderRadius: '4px',
                      }}
                    />
                  )}
                  {description && (
                    <p
                      className="event-description"
                      style={{ flex: '1 1 auto', minWidth: 0, fontSize: '14px', lineHeight: '1.5', margin: 0, opacity: 0.9, whiteSpace: 'pre-line' }}
                    >
                      {description}
                    </p>
                  )}
                </div>
              )}

              <ul className="feature-list" style={{ marginBottom: '24px' }}>
                {event.location && (
                  <li><strong>{strings.pages?.events?.location || 'Location'}:</strong> {event.location}</li>
                )}
              </ul>
            </div>

            {pastPhotos.length > 0 && (
              <details>
                <summary className="event-details-toggle">
                  {strings.lang_code === 'RU'
                    ? 'Нажмите сюда, чтобы посмотреть фото с мероприятия'
                    : 'Click here to see event photos'}
                </summary>
                <div className="past-event-photo-grid">
                  {pastPhotos.map((photo) => {
                    const photoUrl = getEventImageUrl(photo.imgPath || photo.imagePath || photo.image_path || photo.image);
                    return (
                      <article key={photo.id || photoUrl} className="past-event-photo-card">
                        {photoUrl && (
                          <img
                            className="past-event-photo-image"
                            src={photoUrl}
                            alt={`${photo.caption || event.title} - ${photo.description || 'event photo'}`}
                          />
                        )}
                      </article>
                    );
                  })}
                </div>
              </details>
            )}
          </article>
        );
      })}
    </div>
  );
}